/**
 * <scroll-projects> — sections/scroll-projects.liquid
 *
 */
(function () {
  if (customElements.get("scroll-projects")) return;

  const DESKTOP_QUERY = "(min-width: 1100px)";
  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
  const HYSTERESIS = 0.05;
  const JUMP_DURATION = 1.2;
  const STATUS_DEBOUNCE_MS = 450;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }


  function easeInOutPower2(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  class ScrollProjects extends HTMLElement {
    connectedCallback() {
      this.stage = this.closest("[data-spj-stage]");
      this.thumbs = Array.from(this.querySelectorAll("[data-spj-thumb]"));
      this.layers = Array.from(this.querySelectorAll("[data-spj-layer]"));
      this.panels = Array.from(this.querySelectorAll("[data-spj-panel]"));
      this.thumbList = this.querySelector("[data-spj-thumbs]");
      this.status = this.querySelector("[data-spj-status]");

      if (!this.stage || !this.layers.length) return;
      this.activeIndex = 0;
      this.rafId = null;
      this.statusTimer = null;

      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

      this.onScroll = this.onScroll.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onThumbKeydown = this.onThumbKeydown.bind(this);

      this.desktopQuery.addEventListener("change", this.onModeChange);
      this.reducedMotionQuery.addEventListener("change", this.onModeChange);

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener("click", () => this.jumpTo(i));
      });

      if (this.thumbList) this.thumbList.addEventListener("keydown", this.onThumbKeydown);

      this.enterCurrentMode();
    }

    disconnectedCallback() {
      this.teardownPin();
      if (this.desktopQuery) this.desktopQuery.removeEventListener("change", this.onModeChange);
      if (this.reducedMotionQuery) {
        this.reducedMotionQuery.removeEventListener("change", this.onModeChange);
      }
      if (this.thumbList) this.thumbList.removeEventListener("keydown", this.onThumbKeydown);
      window.clearTimeout(this.statusTimer);
    }

    isPinned() {
      return this.desktopQuery.matches && !this.reducedMotionQuery.matches;
    }

    onModeChange() {
      this.teardownPin();
      this.enterCurrentMode();
    }

    enterCurrentMode() {
      if (!this.isPinned()) {
        this.setActiveIndex(0, { announce: false });
        return;
      }
      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onScroll, { passive: true });
      this.readScroll();
    }

    teardownPin() {
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onScroll);
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.readScroll();
      });
    }

    readScroll() {
      const scrollable = this.stage.offsetHeight - window.innerHeight;
      if (scrollable <= 0) return;

      const progress = clamp(-this.stage.getBoundingClientRect().top / scrollable, 0, 1);
      this.commit(progress * this.layers.length);
    }


    commit(raw) {
      const steps = this.layers.length;
      const candidate = clamp(Math.floor(raw), 0, steps - 1);
      if (candidate === this.activeIndex) return;


      const boundary = candidate > this.activeIndex ? candidate : candidate + 1;
      if (Math.abs(raw - boundary) < HYSTERESIS) return;

      this.setActiveIndex(candidate);
    }


    setActiveIndex(index, { announce = true } = {}) {
      if (index === this.activeIndex && this.hasSyncedOnce) return;
 
      const isFirstSync = !this.hasSyncedOnce;
      this.activeIndex = index;
      this.hasSyncedOnce = true;

 
      const skipped = [];
      this.layers.forEach((layer, i) => {
        const from = layer.dataset.state;
        const to = i === index ? "active" : i < index ? "prev" : "next";
        if ((from === "next" && to === "prev") || (from === "prev" && to === "next")) {
          skipped.push(layer);
          layer.classList.add("spj_layer--instant");
        }
        layer.dataset.state = to;
      });

      if (skipped.length) {

        void this.offsetHeight;
        skipped.forEach((layer) => layer.classList.remove("spj_layer--instant"));
      }

      this.panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.dataset.state = isActive ? "active" : i < index ? "prev" : "next";
     
        panel.inert = !isActive;
        if (isActive) {
          panel.removeAttribute("aria-hidden");
        } else {
          panel.setAttribute("aria-hidden", "true");
        }
      });

      this.thumbs.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle("is-active", isActive);
        if (isActive) {
          thumb.setAttribute("aria-current", "true");
        } else {
          thumb.removeAttribute("aria-current");
        }
      });

      if (announce && !isFirstSync) this.announce(index);
    }

  
    announce(index) {
      if (!this.status) return;
      window.clearTimeout(this.statusTimer);
      this.statusTimer = window.setTimeout(() => {
        const title = this.panels[index] && this.panels[index].querySelector(".spj_title");
        this.status.textContent = title ? title.textContent.trim() : "";
      }, STATUS_DEBOUNCE_MS);
    }


    jumpTo(index) {
      if (!this.isPinned()) return;

      const steps = this.layers.length;
      const scrollable = this.stage.offsetHeight - window.innerHeight;
      if (scrollable <= 0 || steps <= 1) return;

      const stageTop = this.stage.getBoundingClientRect().top + window.scrollY;
      const target = stageTop + ((index + 0.5) / steps) * scrollable;

      if (window.lenis) {
        window.lenis.scrollTo(target, { duration: JUMP_DURATION, easing: easeInOutPower2 });
      } else {
        window.scrollTo({ top: target, behavior: "smooth" });
      }
    }

 
    onThumbKeydown(event) {
      const current = this.thumbs.indexOf(document.activeElement);
      if (current === -1) return;

      const last = this.thumbs.length - 1;
      let next = null;

      switch (event.key) {
        case "ArrowDown":
        case "ArrowRight":
          next = Math.min(current + 1, last);
          break;
        case "ArrowUp":
        case "ArrowLeft":
          next = Math.max(current - 1, 0);
          break;
        case "Home":
          next = 0;
          break;
        case "End":
          next = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      if (next === current) return;
      this.thumbs[next].focus();
      this.jumpTo(next);
    }
  }

  customElements.define("scroll-projects", ScrollProjects);
})();
